import React, {useMemo, useState} from 'react';
import type {SxProps} from '@mui/material';
import {useTheme} from '@mui/material';
import type {
  ExpressionV4,
  ManifestationStatus as ManifestationStatusType,
  ManifestationV4,
  WorkMetadata,
} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {getLowestStatus} from 'services/metadata';
import {CheckboxField, FlexBox, Spacer, Text} from 'components';
import {useExpressionTitle} from './ExpressionCard/hooks/useExpressionTitle';
import {ManifestationStatus} from './ManifestationStatus';
import {ManifestationTitle} from './ManifestationTitle';

type Props = {
  manifestation: ManifestationV4;
  currentExpression: ExpressionV4;
  targetWork: WorkMetadata;
  onSetCleanupEmptyWork?: (cleanup: boolean) => void;
  cleanupEmptyWorkInitialState: boolean;
};

const containerSx = {
  width: '60vh',
  minWidth: '600px',
  maxWidth: '800px',
} as const;

const infoSx = {
  flex: 4,
};

export const MoveToExistingWorkInfo: React.FC<Props> = ({
  manifestation,
  currentExpression,
  targetWork,
  cleanupEmptyWorkInitialState,
  onSetCleanupEmptyWork,
}) => {
  const {t} = useLocalization();
  const {colors} = useTheme();
  const labelSx: SxProps = {
    flex: 1,
    fontWeight: 'normal',
    color: colors.warmGreen,
  };
  const expressionTitle = useExpressionTitle(currentExpression, undefined);
  const targetWorkTitle = targetWork.work.preferredTitle?.[0] ?? '';
  const targetWorkStatus = useMemo(() => {
    return targetWork.manifestations.reduce<ManifestationStatusType>(
      (acc, m) => getLowestStatus(acc, m.status) ?? acc,
      'COMPLETED',
    );
  }, [targetWork.manifestations]);

  const [internalCleanupState, setInternalCleanupState] = useState(
    cleanupEmptyWorkInitialState,
  );

  return (
    <FlexBox sx={containerSx}>
      <FlexBox horizontal center>
        <Text sx={labelSx} variant="h4">
          {t('entity.expression.title')}
        </Text>
        <FlexBox sx={infoSx} horizontal>
          <Spacer size={0.75} />
          <Text variant="h4" sx={{fontWeight: 'normal'}}>
            {expressionTitle}
          </Text>
        </FlexBox>
      </FlexBox>
      <Spacer size={1} />
      <FlexBox horizontal center>
        <Text sx={labelSx} variant="h4">
          {t('entity.manifestation.title')}
        </Text>
        <FlexBox sx={infoSx} horizontal>
          <ManifestationTitle
            manifestation={manifestation}
            fontWeight="normal"
          />
          <Spacer size={1} />
          <ManifestationStatus status={manifestation.status} />
        </FlexBox>
      </FlexBox>
      <Spacer size={2} />
      <FlexBox horizontal center>
        <Text sx={labelSx} variant="h4">
          {t('search.work.move.toWork.label')}
        </Text>
        <FlexBox sx={infoSx} horizontal left center>
          <Spacer size={0.75} />
          <Text variant="h4" sx={{fontWeight: 'normal'}}>
            {targetWorkTitle}
          </Text>
          <Spacer size={1} />
          <ManifestationStatus status={targetWorkStatus} />
        </FlexBox>
      </FlexBox>
      {onSetCleanupEmptyWork ? (
        <>
          <Spacer size={2} />
          <FlexBox>
            <Text sx={infoSx} variant="h4">
              Verket du flytter fra har ingen manifestasjoner når du har
              flyttet.
            </Text>
            <Text sx={infoSx} variant="h4">
              Ønsker du å rydde vekk koblinger fra verket du flytter fra?
            </Text>
            <CheckboxField
              fieldId={'cleanupCrew'}
              name={'cleanupCrew'}
              boxLabel={'Fjern gamle koblinger automatisk'}
              value={internalCleanupState}
              onChange={value => {
                setInternalCleanupState(value);
                onSetCleanupEmptyWork(value);
              }}
            />
          </FlexBox>
        </>
      ) : null}
      <Spacer size={2} />
      <Text sx={labelSx} variant="h4">
        {t('search.work.move.footer')}
      </Text>
    </FlexBox>
  );
};
